import React, { FC, useCallback, useRef, useState } from 'react';
import ProductionOrderContentWrapper from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderContentWrapper';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { connect } from 'react-redux';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { OperationSearchItem, ProductionBatchStatistics, ProductionOrderSearchItem } from '@interfaces/search';
import { setProductionOrderId, setProductionOrders } from '@reducers/details';
import { productionOrdersSearch } from '@reducers/search';
import { SearchEntity } from '@interfaces/methods';
import { getLinkingModalConfig, ImageUpload, isPhone, isWeb, Popup, parseRouteParamAsNumber } from '@helpers';
import { fullWidth, scaleOP, width } from '@components/frame';
import { BOTTOM_SHEET_TOP_OFFSET, INFO_SCREEN_LIST_WIDTH, OP_INFO_TABS_WIDTH, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { fontOP } from '@styles/fonts';
import { StyleProp, Text, View } from 'react-native';
import { ProductionOrderListJobId } from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderJobsList';
import i18n from 'i18n-js';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import { getBoxShadowStyle } from '@styles/shadow';
import { Machine, PaginatedResponse } from '@interfaces/machines';
import { KEYS_TAGS, TAG_KEYS } from '@components/imageHandling/common';
import TopPanel from '@components/operatorPanel/components/OperatorPanelInformation/TopPanel';
import ProductionInformation from '@components/productionInformation';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { useBuildImageContextForAdding } from '@components/operatorPanel/hooks/useBuildImageContextForAdding';
import ProductionOrderContentDifferentModes from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderContentDifferentModes';
import { ImageContextForAdding } from '@v2/components/imageHandling/types/types.d';

interface OperatorPanelInformationProductionOrderScreenProps {
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
  selectedItem: Machine;
  productionOrders: ReducerEntityWithLoadingState<ProductionOrderSearchItem[]>;
  setProductionOrders: (orders: ProductionOrderSearchItem[]) => void;
  setProductionOrderId: (productionOrderId: number) => void;
  productionOrdersSearch: SearchEntity<ProductionOrderSearchItem>;
  jobs: ReducerEntityWithLoadingState<OperationSearchItem[]>;
  C: ColorFunction;
}

interface NoResultsProps extends Pick<OperatorPanelInformationProductionOrderScreenProps, 'C' | 'batch' | 'selectedItem'> {
  imageContextForAdding: ImageContextForAdding;
  testID?: string;
}

const NoResults: FC<NoResultsProps> = ({ C, batch, selectedItem, imageContextForAdding, testID }) => {
  const shadowStyles = getBoxShadowStyle({ type: 'Very Wide', C, scheme: 'dark' });

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/Panel [mobile]`, false);

  const onLinkToJobPress = () => {
    const cachedValues = {
      // to use the same values while linking process
      selectedItem: { ...selectedItem },
      productionBatchId: batch.data?.productionBatchId,
      selectedNCFileId: batch.data?.ncFileId,
      partId: selectedItem?.machineStatus?.partDetails?.partId,
    };

    Popup({
      Component: (p: any) => (
        <ProductionInformation
          {...{
            ...p,
            ...cachedValues,
            scale: scaleOP,
            font: fontOP,
            isOperatorPanel: true,
            title: 'Link Batch to Job',
          }}
        />
      ),
      ...getLinkingModalConfig(scaleOP),
      offsetTop: isWeb ? scaleOP(18) : scaleOP(BOTTOM_SHEET_TOP_OFFSET / 2),
      offsetBottom: isWeb ? scaleOP(18) : 0,
      width: isWeb ? fullWidth - scaleOP(fullWidth > 1116 ? 36 : 0) : 1280,
    });
  };

  return (
    <View testID={testID} style={{ height: '100%', justifyContent: 'center', backgroundColor: bg }}>
      <View style={{ position: 'absolute', top: 0, width: '100%', flex: 1 }}>
        <TopPanel title="Production Order">
          <TouchableElement
            onPress={() =>
              ImageUpload({
                ...imageContextForAdding,
                saveToMainStore: true,
              })
            }
          >
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  boxStyle,
                  {
                    backgroundColor: C(pressHoverColor('Button/Light/Only Text')),
                    width: scaleOP(36),
                    height: scaleOP(36),
                    borderRadius: scaleOP(36) / 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                ]}
              >
                <Svg icon="plus" width={scaleOP(22)} height={scaleOP(22)} color={'Link/Light/Primary'} />
              </View>
            )}
          </TouchableElement>
        </TopPanel>
      </View>
      <View style={{ alignSelf: 'center', gap: scaleOP(10) }}>
        <Text style={[fontOP.Body, { color: C('Label/Light/Tertiary') }]}>
          {i18n.t('No Production Order can be shown, because no Job is linked yet.')}
        </Text>
        <TouchableElement onPress={onLinkToJobPress}>
          {({ boxStyle, pressHoverColor }) => (
            <View
              style={[
                boxStyle,
                {
                  backgroundColor: C(pressHoverColor('Label/Light/AlwaysLight Primary')),
                  borderRadius: scaleOP(18),
                  paddingHorizontal: scaleOP(15),
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingTop: scaleOP(30),
                  paddingBottom: scaleOP(32),
                  gap: scaleOP(12),
                },
                shadowStyles,
              ]}
            >
              <Svg icon="linkPlus" color={C(pressHoverColor('Status/Light/Productive'))} width={scaleOP(20)} height={scaleOP(24)} />
              <Text style={[fontOP['Headline Large'], { color: C('Label/Light/AlwaysDark Primary') }]}>{i18n.t('Link To Job')}</Text>
            </View>
          )}
        </TouchableElement>
      </View>
    </View>
  );
};

const OperatorPanelInformationProductionOrderScreen: FC<OperatorPanelInformationProductionOrderScreenProps> = ({
  batch,
  selectedItem,
  productionOrders,
  setProductionOrderId,
  setProductionOrders,
  productionOrdersSearch,
  jobs,
  C,
}) => {
  const navigation = useNavigation<any>();
  const route = useRoute();
  const jobId = route.params?.[scheduledProductionOperationIdParam];

  const [isError, setIsError] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  const selectedJobInProductionOrder = parseRouteParamAsNumber(route.params?.selectedProductionOperationId);
  const singleJobOfProductionOrderSelected = selectedJobInProductionOrder != null && isFinite(+selectedJobInProductionOrder);

  const jobsApiCallRef = useRef<(() => Promise<PaginatedResponse<OperationSearchItem>>) | null>(null);

  const setJobsApiCall = (apiCallFunction: () => Promise<PaginatedResponse<OperationSearchItem>>) => {
    jobsApiCallRef.current = apiCallFunction;
  };

  const isMachinePerspective = jobId == null;
  const defaultLink = isMachinePerspective ? KEYS_TAGS.ProductionBatch : KEYS_TAGS.ProductionOperation;

  const imageLinksForAdding = useBuildImageContextForAdding(jobId, [
    KEYS_TAGS.ProductionOrder as keyof typeof TAG_KEYS,
    defaultLink as keyof typeof TAG_KEYS,
  ]);

  const [selectedProductionOrderIndex, setSelectedProductionOrderIndex] = useState<number>(-1);

  const orderId = route.params?.selectedProductionOrderId;

  useFocusEffect(
    useCallback(() => {
      if (!productionOrders.data?.length) return;

      let newIndex = orderId ? productionOrders.data?.findIndex((po) => po.productionOrderId === orderId) : 0;
      if (newIndex === -1 || newIndex === undefined || newIndex > productionOrders.data.length - 1) {
        newIndex = 0;
      }
      setSelectedProductionOrderIndex(newIndex);
      navigation.setParams({ selectedProductionOrderId: productionOrders.data?.[newIndex]?.productionOrderId });
    }, [orderId, productionOrders.data])
  );

  const [noData, setNoData] = useState<boolean>(false);

  const productionOrderIds = !isMachinePerspective
    ? [jobs.data?.[0]?.productionOrderId]?.filter(Boolean)
    : [...new Set(batch?.data?.links?.map((l) => l.productionOrderId))];

  const productionOrderIdsHash = productionOrderIds.join(', ');

  useFocusEffect(
    useCallback(() => {
      if (productionOrderIds.length === 0) {
        if ((isMachinePerspective && batch.wasLoaded) || (!isMachinePerspective && jobs.wasLoaded)) {
          setNoData(batch.wasLoaded);
        }
        return;
      }

      let cancelled = false;

      (async () => {
        try {
          const result = await productionOrdersSearch({
            data: {},
            filter: { oDataFilter: productionOrderIds.map((id) => `ProductionOrderId eq ${id}`).join(' or ') },
            select: [],
            ignoreHistory: true,
            resetHistory: true,
          });

          if (cancelled) return;

          setNoData(!result?.searchResult || result?.searchResult?.length === 0);

          if (result?.searchResult) {
            setProductionOrders(result?.searchResult);
            if (result.searchResult.length === 1) {
              setProductionOrderId(result.searchResult?.[0]?.productionOrderId);
            }
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Error fetching production orders:', error);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [productionOrderIdsHash, batch.wasLoaded, jobs.wasLoaded])
  );

  const onListItemPress = (jobId: ProductionOrderListJobId) => {
    navigation.setParams({ selectedProductionOperationId: jobId });
  };

  if (noData) {
    return (
      <NoResults
        C={C}
        batch={batch}
        selectedItem={selectedItem}
        imageContextForAdding={{ tags: imageLinksForAdding }}
        testID={TEST_IDS.operatorPanel.information.productionOrder.root}
      />
    );
  }

  return (
    <View testID={TEST_IDS.operatorPanel.information.productionOrder.root} style={{ flex: 1 }}>
      <ProductionOrderContentWrapper
        setNoData={setNoData}
        onListItemPress={onListItemPress}
        scale={scaleOP}
        font={fontOP}
        listStyle={{ paddingTop: scaleOP(23) }}
        withProductionOrdersNavigation
        loading={!productionOrders.wasLoaded}
        productionOrder={productionOrders.data?.[selectedProductionOrderIndex]}
        setIsError={setIsError}
        setTotalCount={setTotalCount}
        setJobsApiCall={setJobsApiCall}
      >
        <ProductionOrderContentDifferentModes
          scale={scaleOP}
          font={fontOP}
          withoutHeader
          withTitle
          displaySingleJobWithoutNavigation
          loading={!productionOrders.wasLoaded}
          productionOrder={productionOrders.data?.[selectedProductionOrderIndex]}
          maxContentWidth={isPhone ? width : fullWidth - scaleOP(INFO_SCREEN_LIST_WIDTH) - scaleOP(OP_INFO_TABS_WIDTH)}
          bg={!isWeb ? 'Background/Light/Field on Panel' : 'Background/Light/Field'}
          jobsApiCall={jobsApiCallRef.current}
          isError={isError}
          totalCount={totalCount}
          closeButtonStyle={{ marginRight: scaleOP(6), ...(singleJobOfProductionOrderSelected ? { marginTop: scaleOP(2) } : {}) }}
          singleJobHeaderContainerStyle={{ marginTop: scaleOP(2) }}
        />
      </ProductionOrderContentWrapper>
    </View>
  );
};

export default connect(
  ({ batch: { batch }, details: { productionOrders, jobs }, machines: { selectedItem } }) => ({ batch, productionOrders, selectedItem, jobs }),
  {
    setProductionOrders,
    setProductionOrderId,
    productionOrdersSearch,
  }
)(ColorWrapper(OperatorPanelInformationProductionOrderScreen));
